<template>
  <!-- EcountPanel — Panel 1: 본사 출하 (ecount) - REQ-FE-TRC-003~005 -->
  <!-- SPEC-CHINA-LOT-FLOW-DASHBOARD-001 REQ-FE-DETAIL-006: 확장 필드 추가 -->
  <a-card class="lot-trace-panel" :loading="loading">
    <template #title>
      <span>{{ $t('lotTrace_panel_ecount') }}</span>
      <a-badge
        :count="panelData.count"
        :overflow-count="999"
        :style="{ marginLeft: '8px', backgroundColor: panelData.count > 0 ? '#52c41a' : '#d9d9d9' }"
      />
      <span v-if="panelData.truncated" style="font-size: 12px; color: #faad14; margin-left: 8px;">
        {{ $t('lotTrace_truncated') }}
        ({{ $t('dealerGsp_total_count', { count: panelData.preCapTotal }) }})
      </span>
    </template>
    <template #extra>
      <a-button
        v-if="value"
        type="link"
        size="small"
        @click="viewAll"
      >{{ $t('lotTrace_actions_viewAll') }}</a-button>
    </template>

    <!-- loading skeleton -->
    <template v-if="loading">
      <a-skeleton active :paragraph="{ rows: 3 }" />
    </template>

    <!-- empty state — REQ-FE-TRC-005 -->
    <template v-else-if="panelData.count === 0">
      <a-empty :description="emptyMessage" />
    </template>

    <!-- table — REQ-FE-TRC-004 -->
    <template v-else>
      <div style="overflow-x: auto;">
        <a-table
          :data-source="panelData.rows"
          :columns="columns"
          :pagination="false"
          row-key="_id"
          size="small"
          :scroll="{ x: 1100 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'lotNumber'">
              <!-- REQ-FE-TRC-011: 셀 → 원본 화면 deep-link -->
              <a-button
                type="link"
                size="small"
                style="padding: 0; min-height: 44px; height: auto;"
                @click="deepLinkToSource(record.lotNumber)"
              >{{ record.lotNumber || '-' }}</a-button>
            </template>
            <template v-else-if="column.key === 'transactionDate'">
              {{ formatDate(record.transactionDate) }}
            </template>
            <template v-else-if="column.key === 'transactionTypeEnum'">
              <a-tag>{{ record.transactionTypeEnum || '-' }}</a-tag>
            </template>
            <!-- REQ-FE-DETAIL-006: 확장 필드 -->
            <template v-else-if="column.key === 'gtinCode'">
              <span class="gtin-mono">{{ record.gtinCode || '-' }}</span>
            </template>
            <template v-else-if="column.key === 'gtinChecksumValid'">
              <a-tag v-if="record.gtinChecksumValid === true" color="success">✓</a-tag>
              <a-tag v-else-if="record.gtinChecksumValid === false" color="error">✗</a-tag>
              <span v-else>-</span>
            </template>
            <template v-else-if="column.key === 'mfgDate'">
              {{ formatDateOnly(record.mfgDate) }}
            </template>
            <template v-else-if="column.key === 'nidsRaw'">
              <!-- 기본 collapse, expand 버튼 -->
              <div v-if="record.nidsRaw">
                <div v-if="expandedNids[record._id]" style="font-family: monospace; font-size: 11px; word-break: break-all;">
                  {{ record.nidsRaw }}
                  <a-button type="link" size="small" @click="toggleNids(record._id)">{{ $t('lotFlow_detail_collapse') }}</a-button>
                </div>
                <a-button v-else type="link" size="small" @click="toggleNids(record._id)">{{ $t('lotFlow_detail_expand') }}</a-button>
              </div>
              <span v-else>-</span>
            </template>
          </template>
        </a-table>
      </div>
    </template>
  </a-card>
</template>

<script>
export default {
  name: 'EcountPanel',
  props: {
    panelData: {
      type: Object,
      default: () => ({ count: 0, preCapTotal: 0, truncated: false, rows: [] }),
    },
    loading: { type: Boolean, default: false },
    value: { type: String, default: '' },
    anomalies: { type: Array, default: () => [] },
  },
  emits: ['navigate'],
  data() {
    return {
      // nidsRaw expand/collapse 상태 per row
      expandedNids: {},
    };
  },
  computed: {
    columns() {
      // 기존 7컬럼 + REQ-FE-DETAIL-006 확장 필드
      return [
        { title: this.$t('lotTrace_col_lotNumber'), dataIndex: 'lotNumber', key: 'lotNumber', width: 170 },
        { title: this.$t('lotTrace_col_transactionDate'), dataIndex: 'transactionDate', key: 'transactionDate', width: 130 },
        { title: this.$t('lotTrace_col_transactionTypeEnum'), dataIndex: 'transactionTypeEnum', key: 'transactionTypeEnum', width: 110 },
        { title: this.$t('lotTrace_col_ecountProductCode'), dataIndex: 'ecountProductCode', key: 'ecountProductCode', width: 140 },
        { title: this.$t('lotTrace_col_customerCode'), dataIndex: 'customerCode', key: 'customerCode', width: 100 },
        { title: this.$t('lotTrace_col_quantity'), dataIndex: 'quantity', key: 'quantity', width: 80, align: 'right' },
        // 확장 필드
        { title: this.$t('lotFlow_detail_field_gtinCode'), dataIndex: 'gtinCode', key: 'gtinCode', width: 150 },
        { title: this.$t('lotFlow_detail_field_gs1CompanyPrefix3'), dataIndex: 'gs1CompanyPrefix3', key: 'gs1CompanyPrefix3', width: 80 },
        { title: this.$t('lotFlow_detail_field_gtinChecksumValid'), dataIndex: 'gtinChecksumValid', key: 'gtinChecksumValid', width: 70 },
        { title: this.$t('lotFlow_detail_field_mfgDate'), dataIndex: 'mfgDate', key: 'mfgDate', width: 120 },
        { title: this.$t('lotFlow_detail_field_employeeCode'), dataIndex: 'employeeCode', key: 'employeeCode', width: 110 },
        { title: this.$t('lotFlow_detail_field_inputUserId'), dataIndex: 'inputUserId', key: 'inputUserId', width: 110 },
        { title: this.$t('lotFlow_detail_field_nidsRaw'), dataIndex: 'nidsRaw', key: 'nidsRaw', width: 120 },
      ];
    },
    emptyMessage() {
      const match = (this.anomalies || []).find((a) =>
        ['not_found', 'missing_ecount'].includes(a.type)
      );
      if (match) {
        const key = match.type === 'not_found'
          ? 'lotTrace_anomaly_not_found'
          : 'lotTrace_anomaly_missing_ecount';
        return this.$t(key);
      }
      return this.$t('lotTrace_emptyState');
    },
  },
  methods: {
    formatDate(val) {
      if (!val) return '-';
      const d = new Date(val);
      if (isNaN(d)) return val;
      return d.toLocaleDateString('zh-CN', { timeZone: 'Asia/Shanghai' });
    },
    formatDateOnly(val) {
      if (!val) return '-';
      // YYYY-MM-DD 형식 직접 반환
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.substring(0, 10);
      const d = new Date(val);
      if (isNaN(d)) return val;
      return d.toISOString().substring(0, 10);
    },
    viewAll() {
      if (!this.value) return;
      this.$emit('navigate', `/ecount-etl/transactions?lotNumber=${encodeURIComponent(this.value)}`);
    },
    deepLinkToSource(lotNumber) {
      if (!lotNumber) return;
      this.$emit('navigate', `/ecount-etl/transactions?lotNumber=${encodeURIComponent(lotNumber)}`);
    },
    toggleNids(id) {
      this.expandedNids = { ...this.expandedNids, [id]: !this.expandedNids[id] };
    },
  },
};
</script>

<style scoped>
.gtin-mono {
  font-family: "SF Mono", "Cascadia Mono", Menlo, monospace;
  font-size: 12px;
}
</style>
